import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-clearstep.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';

/**
 * The tufts Clearstep component
 * @element tufts-clearstep
 * @since 1.0.0
 *
 * @attr src - The URL of the Clearstep iframe
 * @attr show-progress-bar - Whether to show the progress bar (true/false)
 * @attr embed - Whether to embed the iframe (true/false)
 * @attr post-height-url - The URL to post height updates to (defaults to current domain)
 * @attr debug - Enable debug logging
 */
@customElement('tufts-clearstep')
export class TuftsClearstep extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * The URL of the Clearstep iframe
   */
  @property({ type: String, attribute: 'src' })
  src = 'https://tufts.clearstep.health/workflows/tufts_login/';

  /**
   * Whether to show the progress bar
   */
  @property({ type: Boolean, attribute: 'show-progress-bar' })
  showProgressBar = false;

  /**
   * Whether to embed the iframe
   */
  @property({ type: Boolean, attribute: 'embed' })
  embed = true;

  /**
   * The URL to post height updates to (defaults to current domain)
   */
  @property({ type: String, attribute: 'post-height-url' })
  postHeightUrl = '';

  /**
   * Debug mode flag that enables logging of component lifecycle events and state changes.
   */
  @property({ type: Boolean, attribute: 'debug' })
  debug = false;

  /**
   * Default iframe height
   */
  private defaultIframeHeight = '500px';

  /**
   * Utility method for debug logging
   * @private
   */
  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`🔤 [TuftsClearstep] ${message}`, ...args);
    }
  }

  /**
   * Constructs the iframe URL with query parameters
   */
  private getIframeUrl(): string {
    const url = new URL(this.src);
    url.searchParams.set('embed', this.embed ? '1' : '0');
    url.searchParams.set('showProgressBar', this.showProgressBar ? '1' : '0');

    // Use the provided postHeightUrl or default to current domain
    const postHeightUrl =
      this.postHeightUrl ||
      `${window.location.protocol}//${window.location.host}`;
    url.searchParams.set('postHeightUrl', postHeightUrl);

    this.debugLog('Constructed iframe URL', url.toString());
    return url.toString();
  }

  /**
   * Handles messages from the iframe
   */
  private handleMessage(event: MessageEvent): void {
    // Basic origin validation - only accept messages from the iframe source
    try {
      const iframeOrigin = new URL(this.src).origin;
      if (
        event.origin !== iframeOrigin &&
        event.origin !== window.location.origin
      ) {
        this.debugLog('Ignored message from untrusted origin', event.origin);
        return;
      }
    } catch (e) {
      this.debugLog('Error validating message origin', e);
      return;
    }

    const message = event.data;
    this.debugLog('Received message from iframe', message);

    if (message && message.type === 'resize') {
      // Prevent any potential layout thrashing by batching the update
      requestAnimationFrame(() => {
        // Find the iframe and update its height directly
        const iframe = this.querySelector(
          '.clearstep-iframe'
        ) as HTMLIFrameElement;
        if (iframe) {
          iframe.style.height = `${message.height}px`;
          this.debugLog('Updated iframe height', `${message.height}px`);
        }
      });
    }
  }

  /**
   * Disable shadow DOM as requested
   */
  createRenderRoot() {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.debugLog('Component connected to DOM');

    // Bind the handler once to avoid creating new functions on each render
    this._boundMessageHandler = this.handleMessage.bind(this);
    window.addEventListener('message', this._boundMessageHandler);
  }

  disconnectedCallback(): void {
    this.debugLog('Component disconnected from DOM');
    window.removeEventListener('message', this._boundMessageHandler);
    super.disconnectedCallback();
  }

  // Store the bound handler to properly remove it later
  private _boundMessageHandler: (event: MessageEvent) => void;

  /**
   * Handles iframe load event
   * @private
   */
  private handleIframeLoad(e: Event): void {
    this.debugLog('Iframe loaded');
    // Ensure iframe has proper dimensions after loading
    const iframe = e.target as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      // Set default height if not already set
      if (!iframe.style.height) {
        iframe.style.height = this.defaultIframeHeight;
      }
      // Send a message to the iframe to request its height
      iframe.contentWindow.postMessage({ type: 'requestHeight' }, '*');
    }
  }

  /**
   * Component render function
   * @returns TemplateResult
   */
  render(): TemplateResult {
    this.debugLog('Rendering component');
    return html`
      <iframe
        class="clearstep-iframe"
        src="${this.getIframeUrl()}"
        title="Tufts Clearstep"
        scrolling="no"
        @load="${this.handleIframeLoad}"
        style="width: 100%;"
      ></iframe>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-clearstep': TuftsClearstep;
  }
}
